import * as React from 'react';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * ActionsMenuCell — kebab → Radix dropdown for per-row row actions.
 *
 *   <ActionsMenuCell
 *     items={[
 *       { label: 'View', onSelect: () => nav(...) },
 *       { label: 'Cancel service', tone: 'danger', onSelect: cancel },
 *     ]}
 *   />
 */

export interface ActionsMenuItem {
  label: React.ReactNode;
  onSelect?: () => void;
  href?: string;
  disabled?: boolean;
  /** `danger` = destructive (red). */
  tone?: 'default' | 'danger';
  /** Render a divider above this item. */
  separated?: boolean;
}

interface ActionsMenuCellProps {
  items: ActionsMenuItem[];
  ariaLabel?: string;
  className?: string;
}

function ActionsMenuCell({ items, ariaLabel = 'Row actions', className }: ActionsMenuCellProps) {
  return (
    <DropdownPrimitive.Root>
      <DropdownPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            className
          )}
        >
          <MoreVertical className="size-4" aria-hidden />
        </button>
      </DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content
          align="end"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            // Layering: high z-index + portal so it sits above sticky headers,
            // tabs, table-row hover backgrounds, and modals' backdrop-free siblings.
            'z-[60] min-w-[200px] rounded-md p-1',
            // Themed popover surface — see the note in UserMenu.tsx. The old
            // 'bg-amber-700' + white text solved a JIT purge problem that no
            // longer exists, at the cost of an amber panel in a navy product.
            'bg-popover text-popover-foreground',
            // Strong shadow + ring so the menu visibly floats above its content.
            'border border-border-strong ring-1 ring-black/5 shadow-popover',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        >
          {items.map((item, i) => {
            const itemClass = cn(
              'flex cursor-pointer items-center rounded-sm px-3 py-2 text-sm outline-none transition-colors',
              'data-[highlighted]:bg-card-hover',
              item.tone === 'danger'
                ? 'text-danger-dark'
                : 'text-foreground',
              item.disabled && 'opacity-50 pointer-events-none'
            );
            return (
              <React.Fragment key={i}>
                {item.separated && i > 0 && (
                  <DropdownPrimitive.Separator className="my-1 h-px bg-border" />
                )}
                {item.href ? (
                  <DropdownPrimitive.Item asChild disabled={item.disabled}>
                    <a href={item.href} className={itemClass}>
                      {item.label}
                    </a>
                  </DropdownPrimitive.Item>
                ) : (
                  <DropdownPrimitive.Item
                    disabled={item.disabled}
                    onSelect={() => item.onSelect?.()}
                    className={itemClass}
                  >
                    {item.label}
                  </DropdownPrimitive.Item>
                )}
              </React.Fragment>
            );
          })}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}

export { ActionsMenuCell };
export type { ActionsMenuCellProps };
